import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    OnInit,
    QueryList,
    ViewChildren,
    ViewContainerRef,
    inject,
} from '@angular/core';
import { WidgetCategory, WidgetCategoryNames } from '../model/widget.enum';
import { AddWidgetDialog, IWidgetConfig } from '../model/widget.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StorageService } from '@lib/core';
import { SHOW_BUTTON, WIDGET_PREVIEW_MODE } from '../model/widget.constants';

@Component({
    selector: 'add-widget-dialog',
    templateUrl: './add-widget-dialog.component.html',
    styleUrls: ['./add-widget-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AddWidgetDialogComponent implements OnInit {
    readonly injector = inject(Injector);
    data = inject<AddWidgetDialog>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<AddWidgetDialogComponent>>(MatDialogRef);

    storage = inject(StorageService);

    @ViewChildren('widgetContainer', { read: ViewContainerRef }) widgetContainers!: QueryList<ViewContainerRef>;

    categoryWidgetType!: string;
    widgetCategoryType = WidgetCategory;

    availableCategories: string[] = [];

    newWidgetData!: (IWidgetConfig | undefined)[];

    addOrRemoveWidget = new EventEmitter<{ widgetId: number; action: 'add' | 'remove' }>();

    constructor() {
        this.injector = Injector.create({
            providers: [
                { provide: SHOW_BUTTON, useValue: true },
                { provide: WIDGET_PREVIEW_MODE, useValue: true },
            ],
        });
    }

    ngOnInit() {
        const widgets = this.data.widgets ?? [];
        this.categoryWidgetType = widgets[0]?.category ?? '';
        this.createNewArray(widgets);
        this.fillCategories(widgets);
    }

    private async createNewArray(widgets: IWidgetConfig[]) {
        this.newWidgetData = widgets.reduce<IWidgetConfig[]>((result, newWidgetDataItem) => {
            // Manually flatten the widgetsMatrix to get all widget IDs
            const flatMatrixIDs = [];
            for (const row of this.data.widgetsMatrix ?? []) {
                for (const id of row) {
                    if (id !== undefined) {
                        flatMatrixIDs.push(id);
                    }
                }
            }

            // Check if the current widget's ID is included in the manually flattened list of IDs from the matrix
            if (flatMatrixIDs.includes(newWidgetDataItem.id)) {
                result.push(newWidgetDataItem);
            }

            return result;
        }, []);
    }

    selectItem(categoryWidget: string): void {
        this.categoryWidgetType = categoryWidget;
    }

    isContainerInNewArray(container?: IWidgetConfig): boolean {
        if (!container) {
            return false;
        }
        return !!this.newWidgetData?.find((containerOne) => containerOne?.id === container.id);
    }

    closeDialog() {
        this.dialogRef.close();
    }

    async removeWidgetFromList(categoryId: number) {
        const foundWidget = this.data.widgets.find((containerOne) => containerOne?.id === categoryId);
        if (!foundWidget) {
            return;
        }
        const index = this.newWidgetData.findIndex((widget) => widget?.id === categoryId);
        if (index !== -1) {
            this.newWidgetData.splice(index, 1);
        }
        this.addOrRemoveWidget.emit({ widgetId: foundWidget.id, action: 'remove' });
    }

    async addWidgetToList(categoryId: number) {
        const foundWidget = this.data.widgets.find((containerOne) => containerOne?.id === categoryId);
        if (!foundWidget) {
            return;
        }
        this.newWidgetData.push(foundWidget);
        this.addOrRemoveWidget.emit({ widgetId: foundWidget.id, action: 'add' });
    }

    private fillCategories(widgets: IWidgetConfig[]): void {
        widgets.forEach((widget) => {
            if (!this.availableCategories.find((ac) => ac === widget.category)) {
                this.availableCategories.push(widget.category);
            }
        });
    }

    protected readonly WidgetCategoryNames = WidgetCategoryNames;
}
